import chroma from "chroma-js";
import React, { useState, useCallback } from "react";
import { HexColorInput, HexColorPicker } from "react-colorful";
import { darkOrLightColor } from "../../../helpers/darkOrLightColor";
import {
  mix_colors_wrapper,
  mix_tool_wrapper,
  mix_color_picker_wrapper,
  mix_color_title,
  color_picker_wrapper,
  mix_color_result,
  input_hex_color,
  react_colorful,
  section_card,
  subtitle,
  scale_preview,
  scale_color,
  gradient_bar,
  result_hex,
  color_formats_list,
  format_label,
  format_value,
  mix_controls,
  mix_mode_select,
  swap_btn,
  actions_bar,
  action_btn,
  toast,
} from "../../../styles/modules/mix_colors.module.css";

// Textos multiidioma
const TEXTS = {
  es: {
    title: "Mezclador de Colores",
    subtitle: "Combina dos colores y obtén el resultado perfecto",
    colorsToCombine: "Colores a combinar",
    colorScale: "Escala de colores",
    mixResult: "Resultado de la mezcla",
    mixMode: "Modo de mezcla",
    swap: "Intercambiar",
    copyResult: "Copiar resultado",
    copyScale: "Copiar escala",
    randomColors: "Colores aleatorios",
    copied: "¡Copiado!",
  },
  en: {
    title: "Color Mixer",
    subtitle: "Combine two colors and get the perfect result",
    colorsToCombine: "Colors to combine",
    colorScale: "Color scale",
    mixResult: "Mix result",
    mixMode: "Mix mode",
    swap: "Swap",
    copyResult: "Copy result",
    copyScale: "Copy scale",
    randomColors: "Random colors",
    copied: "Copied!",
  },
  pt: {
    title: "Misturador de Cores",
    subtitle: "Combine duas cores e obtenha o resultado perfeito",
    colorsToCombine: "Cores para combinar",
    colorScale: "Escala de cores",
    mixResult: "Resultado da mistura",
    mixMode: "Modo de mistura",
    swap: "Trocar",
    copyResult: "Copiar resultado",
    copyScale: "Copiar escala",
    randomColors: "Cores aleatórias",
    copied: "Copiado!",
  },
  fr: {
    title: "Mélangeur de Couleurs",
    subtitle: "Combinez deux couleurs et obtenez le résultat parfait",
    colorsToCombine: "Couleurs à combiner",
    colorScale: "Échelle de couleurs",
    mixResult: "Résultat du mélange",
    mixMode: "Mode de mélange",
    swap: "Échanger",
    copyResult: "Copier le résultat",
    copyScale: "Copier l'échelle",
    randomColors: "Couleurs aléatoires",
    copied: "Copié!",
  },
  de: {
    title: "Farbmischer",
    subtitle: "Kombiniere zwei Farben und erhalte das perfekte Ergebnis",
    colorsToCombine: "Zu kombinierende Farben",
    colorScale: "Farbskala",
    mixResult: "Mischergebnis",
    mixMode: "Mischmodus",
    swap: "Tauschen",
    copyResult: "Ergebnis kopieren",
    copyScale: "Skala kopieren",
    randomColors: "Zufällige Farben",
    copied: "Kopiert!",
  },
};

// Modos de mezcla disponibles
const MIX_MODES = ["rgb", "lab", "lch", "hsl", "lrgb"];

// Función para convertir a CMYK
function rgbToCmyk(r, g, b) {
  let c = 1 - r / 255;
  let m = 1 - g / 255;
  let y = 1 - b / 255;
  let k = Math.min(c, m, y);
  c = (c - k) / (1 - k) || 0;
  m = (m - k) / (1 - k) || 0;
  y = (y - k) / (1 - k) || 0;
  return `cmyk(${Math.round(c * 100)}%, ${Math.round(m * 100)}%, ${Math.round(
    y * 100
  )}%, ${Math.round(k * 100)}%)`;
}

function MixColors({ locale }) {
  const texts = TEXTS[locale] || TEXTS.es;

  const [hexColor1, setHexColor1] = useState("#3b8cde");
  const [hexColor2, setHexColor2] = useState("#dd00ff");
  const [mixMode, setMixMode] = useState("lch");
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");

  // Resultado de la mezcla
  const colorResult = chroma.mix(hexColor1, hexColor2, 0.5, mixMode);
  const colorResultHex = colorResult.hex().toUpperCase();
  const textColor = darkOrLightColor(colorResult);

  // Escala de colores
  const colorScale = chroma
    .scale([hexColor1, hexColor2])
    .mode(mixMode)
    .colors(7);

  // Gradiente CSS
  const gradientCSS = `linear-gradient(90deg, ${hexColor1}, ${hexColor2})`;

  // Mostrar toast
  const displayToast = useCallback(message => {
    setToastMessage(message);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 2000);
  }, []);

  // Copiar al portapapeles
  const copyToClipboard = useCallback(
    text => {
      navigator.clipboard.writeText(text);
      displayToast(`${texts.copied} ${text}`);
    },
    [displayToast, texts.copied]
  );

  // Intercambiar colores
  const swapColors = useCallback(() => {
    const temp = hexColor1;
    setHexColor1(hexColor2);
    setHexColor2(temp);
  }, [hexColor1, hexColor2]);

  // Colores aleatorios
  const randomColors = useCallback(() => {
    setHexColor1(chroma.random().hex());
    setHexColor2(chroma.random().hex());
  }, []);

  // Copiar escala completa
  const copyScale = useCallback(() => {
    const scaleText = colorScale.join(", ");
    navigator.clipboard.writeText(scaleText);
    displayToast(texts.copied);
  }, [colorScale, displayToast, texts.copied]);

  // Formatos del color resultado
  const colorFormats = [
    { label: "HEX", value: colorResultHex },
    { label: "RGB", value: colorResult.css("rgb") },
    { label: "HSL", value: colorResult.css("hsl") },
    { label: "CMYK", value: rgbToCmyk(...colorResult.rgb()) },
  ];

  return (
    <section className={mix_colors_wrapper}>
      <h1 className={mix_color_title}>
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          style={{ width: 28, height: 28, marginRight: 8 }}
        >
          <circle cx="9" cy="9" r="7" />
          <circle cx="15" cy="15" r="7" />
        </svg>
        {texts.title}
      </h1>
      <p className={subtitle}>{texts.subtitle}</p>

      <div className={mix_tool_wrapper}>
        {/* Colores a combinar */}
        <div className={section_card}>
          <h2>
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <circle cx="13.5" cy="6.5" r="2.5" />
              <circle cx="17.5" cy="10.5" r="2.5" />
              <circle cx="8.5" cy="7.5" r="2.5" />
              <circle cx="6.5" cy="12.5" r="2.5" />
              <path d="M12 22c-4.97 0-9-2.582-9-7v-.088C3 12.794 4.338 11.1 6.375 10c1.949-1.052 3.101-2.99 2.813-5l-.563-3 2.086.795c.64.244 1.064.858 1.064 1.54v.665c0 1.657 1.343 3 3 3h.225c1.553 0 2.775 1.3 2.775 2.85v.65c0 1.657 1.343 3 3 3H22c-.663 2.393-3.006 4.5-6 4.5h-4z" />
            </svg>
            {texts.colorsToCombine}
          </h2>

          <div className={mix_color_picker_wrapper}>
            <div
              className={color_picker_wrapper}
              style={{ backgroundColor: hexColor1 }}
            >
              <HexColorPicker
                className={react_colorful}
                color={hexColor1}
                onChange={setHexColor1}
              />
              <HexColorInput
                className={input_hex_color}
                color={hexColor1}
                onChange={setHexColor1}
              />
            </div>
            <div
              className={color_picker_wrapper}
              style={{ backgroundColor: hexColor2 }}
            >
              <HexColorPicker
                className={react_colorful}
                color={hexColor2}
                onChange={setHexColor2}
              />
              <HexColorInput
                className={input_hex_color}
                color={hexColor2}
                onChange={setHexColor2}
              />
            </div>
          </div>

          {/* Controles */}
          <div className={mix_controls}>
            <select
              className={mix_mode_select}
              value={mixMode}
              onChange={e => setMixMode(e.target.value)}
              title={texts.mixMode}
            >
              {MIX_MODES.map(mode => (
                <option key={mode} value={mode}>
                  {mode.toUpperCase()}
                </option>
              ))}
            </select>
            <button
              className={swap_btn}
              onClick={swapColors}
              title={texts.swap}
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <polyline points="17 1 21 5 17 9" />
                <path d="M3 11V9a4 4 0 0 1 4-4h14" />
                <polyline points="7 23 3 19 7 15" />
                <path d="M21 13v2a4 4 0 0 1-4 4H3" />
              </svg>
            </button>
          </div>

          <div className={actions_bar}>
            <button className={action_btn} onClick={randomColors}>
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M23 4v6h-6" />
                <path d="M1 20v-6h6" />
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
              </svg>
              {texts.randomColors}
            </button>
          </div>
        </div>

        {/* Escala de colores */}
        <div className={section_card}>
          <h2>
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
              <line x1="3" y1="9" x2="21" y2="9" />
              <line x1="3" y1="15" x2="21" y2="15" />
            </svg>
            {texts.colorScale}
          </h2>

          {/* Barra de gradiente */}
          <div className={gradient_bar} style={{ background: gradientCSS }} />

          {/* Colores de la escala */}
          <div className={scale_preview}>
            {colorScale.map((color, i) => (
              <div
                key={i}
                className={scale_color}
                style={{
                  backgroundColor: color,
                  color: darkOrLightColor(color),
                }}
                onClick={() => copyToClipboard(color.toUpperCase())}
                title={color.toUpperCase()}
              >
                <span>{color.toUpperCase().slice(1)}</span>
              </div>
            ))}
          </div>

          <div className={actions_bar}>
            <button className={action_btn} onClick={copyScale}>
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
              </svg>
              {texts.copyScale}
            </button>
          </div>
        </div>

        {/* Resultado */}
        <div className={section_card}>
          <h2>
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z" />
              <path d="M12 6v6l4 2" />
            </svg>
            {texts.mixResult}
          </h2>

          <div
            className={mix_color_result}
            style={{ backgroundColor: colorResultHex, color: textColor }}
            onClick={() => copyToClipboard(colorResultHex)}
          >
            <span className={result_hex}>{colorResultHex}</span>
          </div>

          {/* Formatos */}
          <ul className={color_formats_list}>
            {colorFormats.map(format => (
              <li
                key={format.label}
                onClick={() => copyToClipboard(format.value)}
              >
                <span className={format_label}>{format.label}</span>
                <span className={format_value}>{format.value}</span>
              </li>
            ))}
          </ul>

          <div className={actions_bar}>
            <button
              className={action_btn}
              onClick={() => copyToClipboard(colorResultHex)}
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
              </svg>
              {texts.copyResult}
            </button>
          </div>
        </div>
      </div>

      {/* Toast */}
      {showToast && (
        <div className={toast}>
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            style={{ width: 18, height: 18 }}
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
          {toastMessage}
        </div>
      )}
    </section>
  );
}

export default MixColors;
